import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, ExternalLink, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { toUS } from './AssignRecertModal';

export const ReviewRecertModal = ({ item, onClose, onReviewed }) => {
  const a = item.assignment;
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const submit = async (decision) => {
    if (decision === 'request_changes' && !note.trim()) {
      toast.error('Type what the nurse needs to change');
      return;
    }
    setBusy(decision);
    try {
      const r = await api.post(`/recert/visit/${a.appointmentId}/review`, { decision, note });
      toast.success(r.data.message);
      onReviewed();
      onClose();
      if (r.data.draft485Id) navigate(`/dashboard/clinical/poc-485/edit?id=${r.data.draft485Id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the review');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" data-testid="review-recert-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="bg-gray-900 p-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#d4af37] flex items-center gap-2"><ClipboardCheck className="w-5 h-5" /> Review Recert Assessment</h3>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="review-recert-close"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-gray-600">
            <strong>{item.patientName}</strong>, recert visit of {toUS(a.visitDate)} done by {a.completedBy || a.staffName}. Cert period ends {item.certPeriodEnd}.
          </p>
          <a href={`/dashboard/forms/recertification?id=${a.formId}&appointmentId=${a.appointmentId}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-medium text-[#b8860b] hover:underline" data-testid="review-open-assessment">
            Open the nurse's assessment <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="review-note"
            placeholder="Note for the nurse (required if you request changes)" className="w-full px-3 py-2 border rounded-lg text-sm" />
          <p className="text-xs text-gray-500">Approving creates a draft 485 for the new cert period, filled in from this assessment, the chart and the last 485.</p>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => submit('request_changes')} disabled={!!busy} data-testid="review-request-changes"
              className="py-2.5 rounded-full border border-amber-600 text-amber-700 font-semibold hover:bg-amber-50 disabled:opacity-40 flex items-center justify-center gap-2">
              {busy === 'request_changes' && <Loader2 className="w-4 h-4 animate-spin" />} Request Changes
            </button>
            <button onClick={() => submit('approve')} disabled={!!busy} data-testid="review-approve"
              className="py-2.5 rounded-full bg-[#d4af37] text-gray-900 font-semibold disabled:opacity-40 flex items-center justify-center gap-2">
              {busy === 'approve' && <Loader2 className="w-4 h-4 animate-spin" />} Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
